/**
 * Base Repository
 *
 * Provides common database operations and error handling
 * for all repository implementations.
 */

import { supabase } from '../lib/supabase';

/**
 * Standard error codes for repository operations
 */
export const RepositoryErrorCodes = {
  NOT_FOUND: 'NOT_FOUND',
  DUPLICATE: 'DUPLICATE',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
};

/**
 * Custom repository error class
 */
export class RepositoryError extends Error {
  constructor(code, message, details = null) {
    super(message);
    this.name = 'RepositoryError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Wraps Supabase errors in a consistent format
 */
export function handleSupabaseError(error, context = '') {
  console.error(`Repository error in ${context}:`, error);

  if (error.code === '23505') {
    return new RepositoryError(
      RepositoryErrorCodes.DUPLICATE,
      'A record with this identifier already exists',
      { originalError: error.message }
    );
  }

  if (error.code === '23503') {
    return new RepositoryError(
      RepositoryErrorCodes.VALIDATION_ERROR,
      'Referenced record does not exist',
      { originalError: error.message }
    );
  }

  if (error.code === 'PGRST116') {
    return new RepositoryError(
      RepositoryErrorCodes.NOT_FOUND,
      'Record not found',
      { originalError: error.message }
    );
  }

  return new RepositoryError(
    RepositoryErrorCodes.DATABASE_ERROR,
    error.message || 'An unexpected database error occurred',
    { originalError: error }
  );
}

/**
 * Creates a base repository with common CRUD operations
 *
 * @param {string} tableName - The Supabase table name
 * @returns {Object} Repository object with CRUD methods
 */
export function createBaseRepository(tableName) {
  return {
    /**
     * Find a single record by ID
     */
    async findById(id) {
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw handleSupabaseError(error, `${tableName}.findById`);
      }

      return data;
    },

    /**
     * Find multiple records with optional filters
     */
    async findMany(filters = {}, options = {}) {
      let query = supabase.from(tableName).select('*');

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query = query.eq(key, value);
        }
      });

      // Apply ordering
      if (options.orderBy) {
        query = query.order(options.orderBy, {
          ascending: options.ascending ?? false,
        });
      }

      // Apply pagination
      if (options.limit) {
        query = query.limit(options.limit);
      }
      if (options.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data, error } = await query;

      if (error) {
        throw handleSupabaseError(error, `${tableName}.findMany`);
      }

      return data || [];
    },

    /**
     * Create a new record
     */
    async create(data) {
      const { data: created, error } = await supabase
        .from(tableName)
        .insert(data)
        .select()
        .single();

      if (error) {
        throw handleSupabaseError(error, `${tableName}.create`);
      }

      return created;
    },

    /**
     * Update a record by ID
     */
    async update(id, data) {
      const { data: updated, error } = await supabase
        .from(tableName)
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw handleSupabaseError(error, `${tableName}.update`);
      }

      return updated;
    },

    /**
     * Delete a record by ID
     */
    async delete(id) {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id);

      if (error) {
        throw handleSupabaseError(error, `${tableName}.delete`);
      }

      return true;
    },

    /**
     * Count records with optional filters
     */
    async count(filters = {}) {
      let query = supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true });

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query = query.eq(key, value);
        }
      });

      const { count, error } = await query;

      if (error) {
        throw handleSupabaseError(error, `${tableName}.count`);
      }

      return count || 0;
    },

    /**
     * Check if a record exists
     */
    async exists(filters) {
      const count = await this.count(filters);
      return count > 0;
    },
  };
}

export default {
  createBaseRepository,
  RepositoryError,
  RepositoryErrorCodes,
  handleSupabaseError,
};
